import clsx from 'clsx'

const MAX_BAR = 24
const FLOOR = 3
const STEP = 2.2

/**
 * Twelve calendar months of dated touches (messages and deal appearances),
 * oldest first, the current month last.
 *
 * A contact with nothing dated in the window draws no bars at all - a flat
 * line of zeros would read as "measured, and silent" when the truth is
 * "nothing on record to measure". The bars are decoration for the eye; the
 * title carries the numbers.
 */
export default function Sparkline({
  values,
  tone = 'neutral',
  compact = false,
  className,
}: {
  values: readonly number[]
  tone?: 'neutral' | 'cooling'
  /** Thinner bars, for a table cell that shares its row with eight others. */
  compact?: boolean
  className?: string
}) {
  const total = values.reduce((sum, v) => sum + v, 0)
  if (total === 0) {
    return (
      <span className={clsx('text-[10px] italic text-stone-600', className)} title="No dated activity in the last 12 months">
        none
      </span>
    )
  }
  const title = `Touches per month, last ${values.length} months, oldest first: ${values.join(', ')}`
  return (
    <span
      className={clsx('flex h-6 items-end', compact ? 'gap-px' : 'gap-0.5', className)}
      role="img"
      aria-label={title}
      title={title}
      data-testid="sparkline"
    >
      {values.map((v, i) => (
        <span
          key={i}
          className={clsx(compact ? 'w-[3px]' : 'w-1', 'rounded-[1px]', tone === 'cooling' ? 'bg-orange-400/80' : 'bg-stone-400/80')}
          style={{ height: Math.min(MAX_BAR, FLOOR + v * STEP) }}
        />
      ))}
    </span>
  )
}
